import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Badge, Button, DataTable, DefinitionList, Drawer, Inline, Select, Skeleton, Stack, t, Text, toast } from '@kanbios/ui';
import { useSession } from '../lib/session';
import { changeRef } from './catalogue/PullRequestNotice';
import type { maintenance } from '@kanbios/api-client';
import { api, ApiFailure, unwrap } from '../lib/api';
import { relativeTime } from '../lib/format';
import { FULL_HEIGHT } from '../lib/tables';

type Analysis = maintenance.components['schemas']['DeployAnalysis'];
type Finding = Analysis['findings'][number];

const SEVERITY_TONE = { blocking: 'neg', warning: 'warn', info: 'info' } as const;
const VERDICT_TONE = { to_fix: 'neg', conform_with_warnings: 'warn', conform: 'pos' } as const;
/** Where a finding is, from its params, in the order people look for it. */
const WHERE = ['service', 'task', 'path', 'variable', 'image', 'label', 'branch'] as const;

/** One sentence for a finding: its reason when the rule has several, then where it is. */
export function findingDetail(f: Finding): string {
  const parts: string[] = [];
  if (f.params.reason) parts.push(t(`deployContract.reasons.${f.params.reason}`, f.params));
  for (const key of WHERE) if (f.params[key]) parts.push(t(`deployContract.where.${key}`, { value: f.params[key] }));
  return parts.join(' · ') || '—';
}

/** The findings of an analysis, as in the drawer and at the « Dépôt » step of the installation (one table, DSM DataTable). */
export function FindingsTable({ findings }: { findings: Finding[] }) {
  return (
    <DataTable
      label={t('deployContract.findingsLabel')}
      rows={findings.map((f, i) => ({ ...f, id: `${f.code}-${i}` }))}
      rowKey="id"
      height={FULL_HEIGHT}
      toolbar={false}
      rowHeight="auto"
      columns={[
        { key: 'severity', label: t('deployContract.columns.severity'), width: 130, accessor: (f: Finding) => t(`deployContract.severity.${f.severity}`),
          render: (f: Finding) => <Badge tone={SEVERITY_TONE[f.severity]} size="sm">{t(`deployContract.severity.${f.severity}`)}</Badge> },
        { key: 'rule', label: t('deployContract.columns.rule'), width: 300, wrap: true, accessor: (f: Finding) => `${f.code} ${t(`deployContract.rules.${f.code}.title`)}`,
          render: (f: Finding) => <Stack gap={4}><Text variant="code">{f.code}</Text><Text variant="body-sm">{t(`deployContract.rules.${f.code}.title`)}</Text></Stack> },
        { key: 'detail', label: t('deployContract.columns.detail'), width: 260, wrap: true, accessor: findingDetail },
        { key: 'fix', label: t('deployContract.columns.fix'), grow: true, wrap: true, accessor: (f: Finding) => t(`deployContract.rules.${f.code}.fix`, f.params) },
      ]}
    />
  );
}

/**
 * SPEC-034 § 4 (lot 1.5, tranche 1): the application's repository checked
 * against the deployable repository contract, at a branch (develop by
 * default). Read only: the rules come from deploy-contract.yaml, the same the
 * conformity skill applies.
 */
export function DeployAnalysisDrawer({ application, open, onClose }: { application: string; open: boolean; onClose: () => void }) {
  const [ref, setRef] = useState('develop');
  const analysis = useQuery({
    queryKey: ['deploy-analysis', application, ref],
    queryFn: async () => unwrap(await api.maintenance.GET('/applications/{application}/deploy-analysis', { params: { path: { application }, query: { ref } } })),
    enabled: open,
    retry: false,
    staleTime: 60_000,
  });
  const a = analysis.data;
  const branches = a?.branches ?? ['develop', 'main'];
  const blocking = a?.findings.filter(f => f.severity === 'blocking').length ?? 0;
  const warnings = a?.findings.filter(f => f.severity === 'warning').length ?? 0;
  const error = analysis.error instanceof ApiFailure ? analysis.error.body : null;
  // § 4: « PR de mise en conformité » — the files of a stack template the repository lacks, by pull request.
  const { can } = useSession();
  const [stack, setStack] = useState<'fastapi' | 'nestjs' | 'nextjs' | 'vite' | 'strapi' | 'wordpress'>('fastapi');
  const [proposing, setProposing] = useState(false);
  const missingFiles = a?.findings.some(f => ['DPL-001', 'DPL-006', 'DPL-008', 'DPL-021'].includes(f.code) && ['missing', 'workflow_missing'].includes(f.params.reason ?? '')) ?? false;
  const canPropose = Boolean(a && missingFiles && can('project.create'));
  const propose = async () => {
    setProposing(true);
    try {
      const r = unwrap(await api.maintenance.POST('/applications/{application}/conformity', { params: { path: { application } }, body: { stack, ref } }));
      toast({ tone: 'pos', title: t('deployContract.conformity.opened', { ref: changeRef(r.pullRequest), n: r.files.length }) });
    } catch (err) {
      toast({ tone: 'neg', title: err instanceof ApiFailure ? err.body.message : String(err), description: err instanceof ApiFailure ? err.body.hint : undefined });
    } finally {
      setProposing(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      size="xl"
      eyebrow={t('deployContract.eyebrow')}
      title={t('deployContract.title')}
      subtitle={a ? <Text variant="code" truncate>{a.repository}</Text> : undefined}
      footer={canPropose ? (
        <Inline gap={8} align="end" justify="end">
          <Select label={t('deployContract.conformity.stack')} size="sm" value={stack} onChange={v => v && setStack(v as typeof stack)}
            options={(['fastapi', 'nestjs', 'nextjs', 'vite', 'strapi', 'wordpress'] as const).map(x => ({ value: x, label: t(`deployContract.conformity.stacks.${x}`) }))} />
          <Button icon="merge" disabled={proposing} onClick={() => void propose()}>{t('deployContract.conformity.action')}</Button>
        </Inline>
      ) : undefined}
      headerActions={
        <Inline gap={8} align="center" wrap={false}>
          <Select size="sm" ariaLabel={t('deployContract.branch')} options={[...new Set([ref, ...branches])]} value={ref} onChange={v => v && setRef(v)} />
          <Button variant="secondary" size="sm" icon="refresh" disabled={analysis.isFetching} onClick={() => void analysis.refetch()}>{t('deployContract.again')}</Button>
        </Inline>
      }
    >
      {analysis.isLoading ? <Stack gap={16}><Skeleton lines={2} /><Skeleton lines={6} /></Stack> : error ? (
        <Alert tone={error.code === 'NOT_FOUND_OR_OUT_OF_SCOPE' || error.code === 'CONFLICT' ? 'warn' : 'neg'} title={error.message}>{error.hint}</Alert>
      ) : a && (
        <Stack gap={24}>
          <Alert tone={VERDICT_TONE[a.verdict]} title={t(`deployContract.verdict.${a.verdict}`, { n: a.verdict === 'to_fix' ? blocking : warnings })}>
            {t(`deployContract.verdictBody.${a.verdict}`)}
          </Alert>
          <DefinitionList
            columns={2}
            items={[
              { label: t('deployContract.repository'), value: <Text variant="code">{a.repository}</Text> },
              { label: t('deployContract.branch'), value: <Text variant="code">{a.ref}</Text> },
              { label: t('deployContract.commit'), value: <Text variant="code">{a.commit.slice(0, 7)}</Text> },
              { label: t('deployContract.analysedAt'), value: t('deployContract.contractVersion', { when: relativeTime(a.analysedAt), version: a.contractVersion }) },
            ]}
          />
          {a.findings.length > 0 && <FindingsTable findings={a.findings} />}
          {canPropose && <Alert tone="info" title={t('deployContract.conformity.title')}>{t('deployContract.conformity.subtitle')}</Alert>}
          {a.notChecked.length > 0 && (
            <Stack gap={8}>
              <Text variant="card-title" as="h3">{t('deployContract.notCheckedTitle')}</Text>
              <DefinitionList
                items={a.notChecked.map(n => ({ key: n.code, label: `${n.code} · ${t(`deployContract.rules.${n.code}.title`)}`, value: t(`deployContract.notCheckedReason.${n.reason}`) }))}
              />
            </Stack>
          )}
        </Stack>
      )}
    </Drawer>
  );
}
